import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface Settings {
  language: string;
  streamerMode: boolean;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (updates: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  language: "en",
  streamerMode: true,
};

const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
});

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaultSettings);

  // Load settings from API on mount
  useEffect(() => {
    fetch("/api/getUserSettings", { credentials: "include" })
      .then((res) => {
        if (res.ok) return res.json();
        return null;
      })
      .then((data) => {
        if (data) {
          setSettings({
            language: data.language || "en",
            streamerMode: data.streamer_mode !== false && data.streamer_mode !== 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  const updateSettings = useCallback((updates: Partial<Settings>) => {
    setSettings((prev) => {
      const newSettings = { ...prev, ...updates };

      // Persist to backend
      fetch("/api/updateUserSettings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          language: newSettings.language,
          streamer_mode: newSettings.streamerMode,
        }),
      }).catch(() => {});

      return newSettings;
    });
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
